import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import WebsiteLayout from "./WebsiteLayout";
import { useScrollReveal } from "./useAnimations";
import { Shield, Database, GitBranch, FileText, BarChart3, ArrowRight, Layers, Lock, Workflow, Users, Check, TrendingUp, PieChart, Bell, FolderOpen, LogIn, Handshake, Settings, HeadphonesIcon } from "lucide-react";

const CAPABILITIES = [
  { icon: <Database size={22} />, title: "Fund Accounting", desc: "Enterprise-grade general ledger, investment accounting, and fund accounting workflows with complete audit trails." },
  { icon: <Workflow size={22} />, title: "Operational Workflows", desc: "AI-assisted request management, approval chains, and operational monitoring across your entire organization." },
  { icon: <Layers size={22} />, title: "Data Infrastructure", desc: "Structured financial data architecture with integrity controls and comprehensive access management." },
  { icon: <BarChart3 size={22} />, title: "Financial Reporting", desc: "Institutional reporting engine supporting period closes, consolidated views, and regulatory compliance." },
  { icon: <FileText size={22} />, title: "Document Management", desc: "Centralized document repository with version control, granular permissions, and complete audit transparency." },
  { icon: <Users size={22} />, title: "Investor Operations", desc: "End-to-end investor lifecycle management from onboarding through distributions and portfolio reporting." },
];

const SHOWCASE_SLIDES = [
  {
    src: "/screenshots/inv-dashboard.png",
    icon: <PieChart size={18} />,
    tab: "Dashboard",
    title: "Investor Dashboard",
    desc: "Personalized investor portal with portfolio value, returns tracking, income charts, and quick access to documents and reports.",
  },
  {
    src: "/screenshots/inv-documents.png",
    icon: <FolderOpen size={18} />,
    tab: "Documents",
    title: "Document Center",
    desc: "Access and download tax forms, K-1s, partnership agreements, and fund communications with category filters and search.",
  },
  {
    src: "/screenshots/inv-transactions.png",
    icon: <TrendingUp size={18} />,
    tab: "Transactions",
    title: "Capital Transactions",
    desc: "Full transparency into subscriptions, redemptions, and income with capital and performance tracking views.",
  },
  {
    src: "/screenshots/inv-reporting.png",
    icon: <BarChart3 size={18} />,
    tab: "Reporting",
    title: "Investor Statements",
    desc: "Download quarterly capital account statements and roll-on reports for every reporting period.",
  },
  {
    src: "/screenshots/inv-support.png",
    icon: <Bell size={18} />,
    tab: "Support",
    title: "Support Center",
    desc: "Submit and track support tickets directly with your fund administrator for quick resolution.",
  },
];

const MARQUEE_ITEMS = [
  "Fund Accounting", "General Ledger", "Investor Onboarding", "Document Management",
  "Financial Reporting", "Trade Processing", "Compliance", "Audit Trail",
  "Fund Accounting", "General Ledger", "Investor Onboarding", "Document Management",
  "Financial Reporting", "Trade Processing", "Compliance", "Audit Trail",
];

function HeroVisual() {
  return (
    <div className="w-hero-abstract" data-testid="hero-abstract-visual">
      <div className="w-hero-abstract-glow" />
      <div className="w-hero-abstract-grid">
        <div className="w-abstract-card w-abstract-card--metric" style={{ animationDelay: "0s" }}>
          <div className="w-abstract-card-icon"><Database size={16} /></div>
          <span className="w-abstract-card-label">Fund Accounting</span>
          <div className="w-abstract-card-bar">
            <div className="w-abstract-card-bar-fill" style={{ width: "100%" }} />
          </div>
          <span className="w-abstract-card-status">Fully Integrated</span>
        </div>
        <div className="w-abstract-card w-abstract-card--status" style={{ animationDelay: "0.15s" }}>
          <div className="w-abstract-card-icon"><Shield size={16} /></div>
          <span className="w-abstract-card-label">Security</span>
          <div className="w-abstract-shield-dots">
            <span className="w-abstract-dot w-abstract-dot--active" />
            <span className="w-abstract-dot w-abstract-dot--active" />
            <span className="w-abstract-dot w-abstract-dot--active" />
          </div>
          <span className="w-abstract-card-status">Enterprise-Grade</span>
        </div>
        <div className="w-abstract-card w-abstract-card--chart" style={{ animationDelay: "0.3s" }}>
          <div className="w-abstract-card-icon"><BarChart3 size={16} /></div>
          <span className="w-abstract-card-label">Performance</span>
          <div className="w-abstract-sparkline">
            <svg viewBox="0 0 120 40" fill="none">
              <path d="M0 35 L15 28 L30 30 L45 20 L60 22 L75 12 L90 15 L105 8 L120 5" stroke="rgba(75,113,232,0.8)" strokeWidth="2" fill="none" />
              <path d="M0 35 L15 28 L30 30 L45 20 L60 22 L75 12 L90 15 L105 8 L120 5 L120 40 L0 40Z" fill="url(#sparkGrad)" />
              <defs><linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgba(75,113,232,0.2)" /><stop offset="100%" stopColor="rgba(75,113,232,0)" /></linearGradient></defs>
            </svg>
          </div>
        </div>
        <div className="w-abstract-card w-abstract-card--activity" style={{ animationDelay: "0.45s" }}>
          <div className="w-abstract-card-icon"><Bell size={16} /></div>
          <span className="w-abstract-card-label">Recent Activity</span>
          <div className="w-abstract-activity-lines">
            <div className="w-abstract-activity-line" /><div className="w-abstract-activity-line" /><div className="w-abstract-activity-line" />
          </div>
        </div>
      </div>
      <div className="w-hero-abstract-ring" />
      <div className="w-hero-abstract-ring w-hero-abstract-ring--2" />
    </div>
  );
}

function ProductShowcase() {
  const [active, setActive] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((idx) => {
    if (idx === active || isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActive(idx);
      setIsTransitioning(false);
    }, 250);
  }, [active, isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActive((prev) => (prev + 1) % SHOWCASE_SLIDES.length);
        setIsTransitioning(false);
      }, 250);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const slide = SHOWCASE_SLIDES[active];

  return (
    <section className="w-showcase-v2" data-testid="product-showcase">
      <div className="w-showcase-v2-inner">
        <div className="w-showcase-v2-header w-reveal">
          <div className="w-label" style={{ justifyContent: "center" }}>Proprietary Platform</div>
          <h2 className="w-title" style={{ maxWidth: 600, margin: "0 auto 1rem" }}>
            Purpose-Built for <strong>Fund Operations</strong>
          </h2>
          <p className="w-subtitle">
            Explore the Tychi platform — every module designed for institutional-grade fund administration.
          </p>
        </div>

        {/* Feature Highlight Cards */}
        <div className="w-investor-features w-reveal w-delay-1" data-testid="platform-features">
          {[
            { icon: <PieChart size={20} />, title: "Dashboard", desc: "Portfolio overview with real-time valuations, returns tracking, and income distribution charts." },
            { icon: <BarChart3 size={20} />, title: "Reporting", desc: "Access detailed financial reports, statements, and performance analytics on demand." },
            { icon: <TrendingUp size={20} />, title: "Transactions", desc: "Full transparency into capital calls, distributions, transfers, and transaction history." },
            { icon: <FolderOpen size={20} />, title: "Documents", desc: "Download tax documents, K-1s, quarterly reports, and fund communications securely." },
          ].map((feat, i) => (
            <div key={i} className="w-investor-feature-card" data-testid={`platform-feature-${i}`}>
              <div className="w-investor-feature-icon">{feat.icon}</div>
              <h4>{feat.title}</h4>
              <p>{feat.desc}</p>
            </div>
          ))}
        </div>

        {/* Navigation Tabs */}
        <div className="w-showcase-v2-tabs w-reveal w-delay-1" data-testid="showcase-tabs">
          {SHOWCASE_SLIDES.map((s, i) => (
            <button
              key={i}
              className={`w-showcase-v2-tab ${i === active ? "active" : ""}`}
              onClick={() => goTo(i)}
              data-testid={`showcase-tab-${i}`}
            >
              {s.icon}
              <span>{s.tab}</span>
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="w-showcase-v2-content w-reveal w-delay-2">
          <div className={`w-showcase-v2-preview ${isTransitioning ? "fading" : ""}`} data-testid="showcase-preview">
            <div className="w-screenshot-modern">
              <img src={slide.src} alt={slide.title} />
            </div>
          </div>
          <div className={`w-showcase-v2-info ${isTransitioning ? "fading" : ""}`}>
            <h3 className="w-showcase-v2-title">{slide.title}</h3>
            <p className="w-showcase-v2-desc">{slide.desc}</p>
          </div>
        </div>

        {/* Progress Dots */}
        <div className="w-showcase-v2-dots">
          {SHOWCASE_SLIDES.map((_, i) => (
            <button
              key={i}
              className={`w-showcase-v2-dot ${i === active ? "active" : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  useScrollReveal();

  return (
    <WebsiteLayout>
      {/* Hero */}
      <section className="w-hero" data-testid="hero-section">
        <div className="w-hero-bg" />
        <div className="w-hero-grid-bg" />
        <div className="w-hero-content">
          <div className="w-hero-inner">
            <div>
              <div className="w-hero-badge w-reveal">
                <span className="w-hero-badge-dot" />
                Institutional Fund Administration
              </div>
              <h1 className="w-reveal w-delay-1">
                Intelligent Infrastructure for{" "}
                <span className="w-text-gradient">Fund Administration</span>
              </h1>
              <p className="w-hero-desc w-reveal w-delay-2">
                A unified, AI-enabled platform integrating fund accounting, general ledger, 
                operational workflows, and institutional reporting — powering our managed fund administration services.
              </p>
              <div className="w-hero-actions w-reveal w-delay-3">
                <Link to="/contact" className="w-btn w-btn-primary" data-testid="hero-contact-btn">
                  Schedule a Consultation <ArrowRight size={16} />
                </Link>
                <Link to="/platform" className="w-btn w-btn-ghost" data-testid="hero-explore-btn">
                  Explore Platform
                </Link>
              </div>
            </div>
            <div className="w-hero-visual w-reveal-scale w-delay-2">
              <HeroVisual />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="w-section" style={{ paddingTop: "4rem" }}>
        <div className="w-stats-bar w-reveal">
          <div><div className="w-stat-num">100%</div><div className="w-stat-text">Audit-Ready</div></div>
          <div><div className="w-stat-num">24/5</div><div className="w-stat-text">Availability</div></div>
          <div><div className="w-stat-num">Real-time</div><div className="w-stat-text">Processing</div></div>
          <div><div className="w-stat-num">Multi-fund</div><div className="w-stat-text">Architecture</div></div>
        </div>
      </section>

      {/* Marquee */}
      <div className="w-marquee-wrap">
        <div className="w-marquee-track">
          {MARQUEE_ITEMS.map((item, i) => (
            <span key={i} className="w-marquee-item">
              {item} <span style={{ color: "var(--w-blue)", margin: "0 0.5rem", opacity: 0.4 }}>&bull;</span>
            </span>
          ))}
        </div>
      </div>

      {/* Capabilities */}
      <section className="w-section">
        <div className="w-reveal">
          <div className="w-label">Core Capabilities</div>
          <h2 className="w-title">Everything Your Fund Operations <strong>Require</strong></h2>
          <p className="w-subtitle" style={{ marginBottom: "3.5rem" }}>
            Purpose-built modules that work together to eliminate operational complexity 
            and deliver institutional-grade reliability.
          </p>
        </div>
        <div className="w-grid-3">
          {CAPABILITIES.map((c, i) => (
            <div key={i} className={`w-card w-reveal w-delay-${(i % 3) + 1}`}>
              <div className="w-card-icon">{c.icon}</div>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product Showcase */}
      <ProductShowcase />

      {/* Portal Module */}
      <section className="w-section">
        <div className="w-split">
          <div className="w-reveal-left">
            <div className="w-label">Module 01</div>
            <h2 className="w-title">Tychi <strong>Portal</strong></h2>
            <p style={{ color: "var(--w-text-muted)", lineHeight: 1.75, marginBottom: "1.5rem" }}>
              Operational and workflow platform handling organization onboarding, 
              document management, request approvals, client interactions, and notification systems.
            </p>
            <ul className="w-features">
              {["Investor onboarding with guided workflows", "Centralized document repository", "Multi-level approval chains", "Real-time notification system", "Role-based data access"].map((f, i) => (
                <li key={i}><span className="check-icon"><Check size={12} /></span> {f}</li>
              ))}
            </ul>
          </div>
          <div className="w-reveal-right w-delay-2">
            <div className="w-screenshot-modern" data-testid="portal-screenshot">
              <img src="/screenshots/fm-dashboard.png" alt="Tychi Portal - Fund Manager Dashboard" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* GL Module */}
      <section className="w-section-full w-section-surface">
        <div className="w-section" style={{ paddingTop: "6rem", paddingBottom: "6rem" }}>
          <div className="w-split reverse">
            <div className="w-reveal-right">
              <div className="w-label">Module 02</div>
              <h2 className="w-title">Tychi <strong>GL</strong></h2>
              <p style={{ color: "var(--w-text-muted)", lineHeight: 1.75, marginBottom: "1.5rem" }}>
                Core financial accounting infrastructure powering general ledger management, 
                investment accounting, transaction tracking, financial reporting, and audit-ready records.
              </p>
              <ul className="w-features">
                {["Multi-level chart of accounts", "Investment accounting & trade tracking", "Automated financial reporting", "Period-end close workflows", "Complete audit trail"].map((f, i) => (
                  <li key={i}><span className="check-icon"><Check size={12} /></span> {f}</li>
                ))}
              </ul>
            </div>
            <div className="w-reveal-left w-delay-2">
              <div className="w-screenshot-modern" data-testid="gl-screenshot">
                <img src="/screenshots/analytics-dashboard.png" alt="Tychi GL - Dashboard" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="w-section">
        <div className="w-reveal" style={{ marginBottom: "3rem" }}>
          <div className="w-label">System Design</div>
          <h2 className="w-title">Platform <strong>Architecture</strong></h2>
          <p className="w-subtitle">A modular, scalable architecture designed for institutional financial operations.</p>
        </div>
        <div className="w-arch w-reveal-scale">
          {[
            { icon: <GitBranch size={28} />, t: "Workflow Engine", d: "Request & approval automation" },
            { icon: <Database size={28} />, t: "Data Layer", d: "Structured financial data" },
            { icon: <BarChart3 size={28} />, t: "Reporting Engine", d: "Institutional-grade analytics" },
            { icon: <Users size={28} />, t: "Client Portal", d: "Investor self-service" },
            { icon: <Layers size={28} />, t: "Accounting Core", d: "GL & investment accounting" },
            { icon: <Shield size={28} />, t: "Security Layer", d: "Encryption & access control" },
          ].map((a, i) => (
            <div key={i} className="w-arch-item">
              <div style={{ color: "var(--w-blue)" }}>{a.icon}</div>
              <h4>{a.t}</h4><p>{a.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Managed Services */}
      <section className="w-section-full w-section-surface" data-testid="managed-services-section">
        <div className="w-section" style={{ paddingTop: "6rem", paddingBottom: "6rem" }}>
          <div className="w-reveal" style={{ marginBottom: "3rem" }}>
            <div className="w-label">How We Deliver</div>
            <h2 className="w-title">Managed Fund Administration <strong>Services</strong></h2>
            <p className="w-subtitle" style={{ maxWidth: 640, marginBottom: "1rem" }}>
              Tychi is the proprietary technology that powers our end-to-end managed fund administration. 
              It is not sold as standalone software — clients engage Fundtec for a fully managed service, 
              with Tychi operating as the intelligent backbone behind every workflow.
            </p>
          </div>
          <div className="w-grid-3 w-reveal w-delay-1">
            <div className="w-card">
              <div className="w-card-icon"><Handshake size={22} /></div>
              <h3>Fully Managed</h3>
              <p>
                Our team operates Tychi on your behalf — handling fund accounting, investor operations, 
                and regulatory reporting so your team can focus on investment strategy.
              </p>
            </div>
            <div className="w-card">
              <div className="w-card-icon"><Settings size={22} /></div>
              <h3>Technology-Driven</h3>
              <p>
                Every managed engagement is powered by Tychi's AI-enabled modules, ensuring consistency, 
                accuracy, and institutional-grade reliability across all operations.
              </p>
            </div>
            <div className="w-card">
              <div className="w-card-icon"><HeadphonesIcon size={22} /></div>
              <h3>Dedicated Support</h3>
              <p>
                Clients receive hands-on service from experienced fund administrators backed by 
                Tychi's real-time dashboards, automated workflows, and proactive monitoring.
              </p>
            </div>
          </div>
          <div className="w-reveal w-delay-2" style={{ textAlign: "center", marginTop: "2.5rem" }}>
            <Link to="/contact" className="w-btn w-btn-primary" data-testid="managed-services-cta">
              Learn About Our Services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="w-section-full w-section-surface">
        <div className="w-section" style={{ paddingTop: "6rem", paddingBottom: "6rem" }}>
          <div className="w-split">
            <div className="w-reveal-left">
              <div className="w-label">Security & Reliability</div>
              <h2 className="w-title">Financial-Grade <strong>Security</strong></h2>
              <p className="w-subtitle">
                Built with institutional security standards at every layer. From data encryption 
                to role-based access controls, every component is designed for financial-grade reliability.
              </p>
              <ul className="w-features">
                <li><span className="check-icon"><Lock size={12} /></span> End-to-end encryption at rest and in transit</li>
                <li><span className="check-icon"><Shield size={12} /></span> Role-based access control with granular permissions</li>
                <li><span className="check-icon"><FileText size={12} /></span> Complete audit trail for all operations</li>
                <li><span className="check-icon"><Database size={12} /></span> Redundant storage with automated backups</li>
              </ul>
            </div>
            <div className="w-reveal-right w-delay-2" style={{ display: "flex", justifyContent: "center" }}>
              <div className="w-security-grid-2x2" data-testid="security-grid">
                <div className="w-security-grid-item">
                  <Lock size={28} style={{ color: "var(--w-blue)" }} />
                  <h4>Encrypted</h4>
                  <p>AES-256 at rest & in transit</p>
                </div>
                <div className="w-security-grid-item">
                  <Shield size={28} style={{ color: "var(--w-blue)" }} />
                  <h4>Protected</h4>
                  <p>Role-based access control</p>
                </div>
                <div className="w-security-grid-item">
                  <FileText size={28} style={{ color: "var(--w-blue)" }} />
                  <h4>Audited</h4>
                  <p>Full compliance trail</p>
                </div>
                <div className="w-security-grid-item">
                  <Database size={28} style={{ color: "var(--w-blue)" }} />
                  <h4>Redundant</h4>
                  <p>Multi-region backups</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portal Access */}
      <section className="w-section" data-testid="investor-login-section">
        <div className="w-reveal" style={{ marginBottom: "3rem" }}>
          <div className="w-label">Client Access</div>
          <h2 className="w-title">Portal <strong>Access</strong></h2>
          <p className="w-subtitle">Securely access the Tychi platform — whether you're a fund manager or an investor.</p>
        </div>
        <div className="w-investor-login w-reveal w-delay-1">
          <div className="w-investor-login-content">
            <h3>Portal Login</h3>
            <p>
              Fund managers and investors can access their personalized dashboards to manage operations, 
              view portfolio performance, download documents, and track transactions.
            </p>
            <div className="w-investor-login-actions">
              <Link to="/login" className="w-btn w-btn-primary" data-testid="investor-login-btn">
                <LogIn size={16} /> Login to Portal
              </Link>
              <Link to="/contact" className="w-btn w-btn-ghost" data-testid="investor-request-btn">
                Request Access
              </Link>
            </div>
          </div>
          <div className="w-investor-login-visual">
            <div className="w-screenshot-modern" data-testid="investor-dashboard-preview">
              <img src="/screenshots/investor-dashboard-full.png" alt="Investor Dashboard Preview" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-cta-section">
        <div className="w-reveal">
          <h2 className="w-title" style={{ maxWidth: 600, margin: "0 auto 1rem" }}>
            Ready to Transform Your <strong>Fund Operations</strong>?
          </h2>
          <p className="w-subtitle" style={{ maxWidth: 450, margin: "0 auto 2.5rem" }}>
            Speak with our team to learn how Tychi can transform your financial infrastructure.
            <br /><span style={{ fontSize: "0.85em", opacity: 0.7 }}>Delivered as part of our managed fund administration services.</span>
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/contact" className="w-btn w-btn-primary" data-testid="cta-contact-btn">
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/platform" className="w-btn w-btn-ghost">Explore Platform</Link>
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}
